/**
 * Regulatory labelling metadata — single source of truth for every value
 * displayed in the "About ESCloud" dialog and the PDF report labelling
 * strip (ESCLOUD-76 / ESCLOUD-180).
 */

import { RUNTIME_CONFIG } from './runtime-config';

export const MANUFACTURER = {
    name: 'Therenva SAS',
    address: '74F rue de Paris, 35000 RENNES, France',
    email: 'contact@therenva.com',
    website: 'www.therenva.com',
} as const;

export const PRODUCT_NAME = 'ESCloud';
export const PRODUCT_DESCRIPTION = 'Medical Image Analysis Software';

/** UDI-DI: GTIN assigned by GS1 France — identifies the device model, fixed across releases. */
export const UDI_DI = '03760262480121';

/** CE notified body number: TUV Rheinland. */
export const CE_NOTIFIED_BODY_NUMBER = '0197';

/** Semver part of the app version, without the `+<commit>` build-metadata suffix. */
export function toSoftwareVersion(appVersion: string): string {
    return appVersion.split('+')[0];
}

/** AI (8012) software version: dots removed, e.g. `1.2.3` → `123`. */
export function toAi8012Version(appVersion: string): string {
    return toSoftwareVersion(appVersion).replace(/\./g, '');
}

/**
 * AI (11) manufacturing date (YYMMDD) derived from a YYYY-MM release date.
 * Only year and month are encoded; the day is `00` as allowed by the GS1
 * General Specifications when the day is not specified — this avoids drift
 * between artifact creation and effective release date, consistent with
 * other Therenva products.
 */
export function toMfgDateYYMM00(releaseDate: string): string {
    const match = /^\d{2}(\d{2})-(0[1-9]|1[0-2])$/.exec(releaseDate);
    if (!match) {
        throw new Error(`RELEASE_DATE must be YYYY-MM, got "${releaseDate}"`);
    }
    return `${match[1]}${match[2]}00`;
}

export interface GS1Elements {
    /** (01) UDI-DI */
    readonly ai01: string;
    /** (11) Manufacturing date */
    readonly ai11: string;
    /** (8012) Version model */
    readonly ai8012: string;
    /** Concatenated version to be used for datamatrix encoding */
    readonly elementString: string;
}

/** GS1 element string encoded in the UDI DataMatrix. */
export function buildGs1Elements(appVersion: string, releaseDate: string): GS1Elements {
    const ai01 = `(01)${UDI_DI}`;
    const ai11 = `(11)${toMfgDateYYMM00(releaseDate)}`;
    const ai8012 = `(8012)${toAi8012Version(appVersion)}`;
    const elementString = `${ai01}${ai11}${ai8012}`;

    return { ai01, ai11, ai8012, elementString };
}

/**
 * Labelling values resolved for the running deployment. `__APP_VERSION__`
 * (UDI-PI) is baked at build time — intrinsic to the artifact — while
 * releaseDate and fda510kNumber come from the deploy-time runtime config
 * (ESCLOUD-248), so one env-agnostic build can be promoted anywhere.
 */
export const REGULATORY = {
    softwareVersion: toSoftwareVersion(__APP_VERSION__),
    /** UDI-PI: production identifier, changes with every release. */
    udiPi: toSoftwareVersion(__APP_VERSION__),
    /** Date of manufacture, displayed as YYYY-MM. */
    releaseDate: RUNTIME_CONFIG.releaseDate,
    /** FDA 510(k) clearance number — null while clearance is pending. */
    fda510kNumber: RUNTIME_CONFIG.fda510kNumber || null,
    /** GS1 elements */
    gs1: buildGs1Elements(__APP_VERSION__, RUNTIME_CONFIG.releaseDate),
} as const;
